<template>
  <div>
    <div class="inner">
      <div class="header">
        <h1>{{ isEditing ? 'Edit' : 'New' }} Volatility Model</h1>
      </div>
      <!-- snackbar -->
      <div
        v-if="snackbar"
        class="tw:fixed tw:left-1/2 tw:top-4 tw:z-50 tw:-translate-x-1/2 tw:min-w-80 tw:max-w-lg"
      >
        <Alert
          :type="snackbarMsg.error ? 'error' : 'success'"
          closable
          @close="snackbar = false"
        >
          <div class="tw:flex tw:items-center tw:gap-4">
            <LucideBadgeCheck :size="20" class="tw:shrink-0" />
            <div>
              <strong>{{ snackbarMsg.title }}</strong>
              <br />
              {{ snackbarMsg.subTitle }}
            </div>
          </div>
        </Alert>
      </div>
      <PageProgress
        class="loader"
        v-if="loading" />
      <div
        class="content mt-8"
        v-else>
        <form
          class="form"
          @submit.prevent="save">
          <div class="fields">
            <fieldset>
              <div>
                <h2>General</h2>
                <p>Input client name, financial reporting period end-date, and current retention value.</p>
              </div>
              <div class="general-fields tw:pt-2">
                <TextField
                  :disabled="saving"
                  v-model="volatilityModel.name"
                  class="name text-field"
                  label="* Model Name"
                  placeholder=" "
                  data-testid="volatility-model-name"
                  :error="nameErrors.length > 0"
                  :error-message="nameErrors.join(' ')"
                  @update:model-value="touch('name')" />

                <div class="client tw:flex tw:flex-col tw:items-stretch">
                  <Combobox
                    id="client-select"
                    data-testid="volatility-client"
                    :model-value="accountNameModelValue"
                    @update:model-value="handleAccountNameChange"
                    :options="clientsForCombobox"
                    :disabled="saving || fromAccountView"
                    label="* Client"
                    placeholder="Select or type to search..."
                    :input-class="`tw:input tw:input-bordered tw:w-full ${accountNameErrors.length ? 'tw:border-red-500' : ''} ${accountNameMatch ? 'tw:pr-10' : ''}`"
                    class="text-field" />
                </div>

                <div class="public-data tw:flex tw:items-start tw:gap-4">
                  <TextField
                    :model-value="stockTicker ?? ''"
                    :disabled="saving || fromAccountView"
                    label="Ticker"
                    class="text-field tw:min-w-0 tw:flex-1"
                    placeholder=" "
                    :error="publicData.error"
                    :error-message="publicData.errorMessages"
                    @update:model-value="(v) => { stockTicker = v === '' ? undefined : v }"
                  />
                  <AppButton
                    class="tw:min-w-36 tw:shrink-0 tw:mb-1 tw:mt-auto"
                    primary
                    :loading="publicDataLoading"
                    :disabled="saving || !isStockTickerEntered"
                    @click="fetchPublicData">
                    Get Public Data
                  </AppButton>
                </div>

                <TextField
                  v-model="volatilityModel.financialYearEnd"
                  label="* Financial Year-End"
                  hint="mm/dd/yyyy"
                  :disabled="saving"
                  placeholder=" "
                  class="financialYearEnd text-field"
                  data-testid="volatility-financial-year-end"
                  :error="errors.financialYearEnd.length > 0"
                  :error-message="errors.financialYearEnd.join(' ')"
                  @update:model-value="touch('financialYearEnd'); validateFinancialYearEnd()"
                />

                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.retention1"
                  prefix="$"
                  class="retention1 text-field"
                  label="* Retention A"
                  data-testid="volatility-retention-a"
                  :error="retention1Errors.length > 0"
                  :error-message="retention1Errors.join(' ')"
                  @update:model-value="touch('retention1')" />
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.retention2"
                  prefix="$"
                  class="retention2 text-field"
                  label="Retention B"
                  :error="retention2Errors.length > 0"
                  :error-message="retention2Errors.join(' ')"
                  @update:model-value="touch('retention2')" />
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.retention3"
                  prefix="$"
                  class="retention3 text-field"
                  label="Retention C"
                  :error="retention3Errors.length > 0"
                  :error-message="retention3Errors.join(' ')"
                  @update:model-value="touch('retention3')" />
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.retention4"
                  prefix="$"
                  class="retention4 text-field"
                  label="Retention D"
                  :error="retention4Errors.length > 0"
                  :error-message="retention4Errors.join(' ')"
                  @update:model-value="touch('retention4')" />
              </div>
            </fieldset>
            <fieldset>
              <div>
                <h2>Annual Performance Indicators</h2>
                <p>
                  Input reported values for the associated financial metrics you’d like to test. For those not applicable or for which reported values
                  are not available, simply leave blank.
                </p>
              </div>
              <div class="pt-2">
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.operatingIncome"
                  prefix="$"
                  id="operating-income"
                  class="operating-income text-field"
                  label="Operating Income"
                  data-testid="volatility-operating-income"
                  :error="operatingIncomeErrors.length > 0"
                  :error-message="operatingIncomeErrors.join(' ')"
                  @update:model-value="touch('operatingIncome')" />
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.netIncome"
                  prefix="$"
                  class="net-income text-field"
                  label="Net Income"
                  data-testid="volatility-net-income"
                  :error="netIncomeErrors.length > 0"
                  :error-message="netIncomeErrors.join(' ')"
                  @update:model-value="touch('netIncome')" />
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.ebitda"
                  prefix="$"
                  class="ebitda text-field"
                  label="EBITDA"
                  data-testid="volatility-ebitda"
                  :error="ebitdaErrors.length > 0"
                  :error-message="ebitdaErrors.join(' ')"
                  @update:model-value="touch('ebitda')" />
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.ebitdaMargin"
                  suffix="%"
                  class="ebitda-margin text-field"
                  label="EBITDA Margin"
                  data-testid="volatility-ebitda-margin"
                  :error="ebitdaMarginErrors.length > 0"
                  :error-message="ebitdaMarginErrors.join(' ')"
                  @update:model-value="touch('ebitdaMargin', 'annualRevenue')" />
                <NumberInput
                  placeholder=" "
                  :disabled="saving || !volatilityModel.ebitdaMargin"
                  v-model="volatilityModel.annualRevenue"
                  prefix="$"
                  class="annual-revenue text-field"
                  label="Annual Revenue"
                  data-testid="volatility-annual-revenue"
                  :error="annualRevenueErrors.length > 0"
                  :error-message="annualRevenueErrors.join(' ')"
                  hint="Required for EBITDA Margin % calculations"
                  @update:model-value="touch('annualRevenue')" />
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.cashFlowFromOperations"
                  prefix="$"
                  class="cash-flow text-field"
                  data-testid="volatility-cash-flow-operations"
                  :error="cashFlowFromOperationsErrors.length > 0"
                  :error-message="cashFlowFromOperationsErrors.join(' ')"
                  @update:model-value="touch('cashFlowFromOperations')"
                  label="Cash Flow From Operations" />
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.freeCashFlow"
                  prefix="$"
                  class="free-cash-flow text-field"
                  data-testid="volatility-free-cash-flow"
                  label="Free Cash Flow"
                  :error="freeCashFlowErrors.length > 0"
                  :error-message="freeCashFlowErrors.join(' ')"
                  @update:model-value="touch('freeCashFlow')" />
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.earningsPerShare"
                  prefix="$"
                  class="eps text-field"
                  data-testid="volatility-earnings-per-share"
                  label="Earnings Per Share"
                  :error="earningsPerShareErrors.length > 0"
                  :error-message="earningsPerShareErrors.join(' ')"
                  @update:model-value="touch('earningsPerShare', 'sharesOutstanding')" />
                <NumberInput
                  placeholder=" "
                  :disabled="saving || !volatilityModel.earningsPerShare"
                  v-model="volatilityModel.sharesOutstanding"
                  class="shares-outstanding text-field"
                  label="Shares Outstanding"
                  data-testid="volatility-shares-outstanding"
                  :error="sharesOutstandingErrors.length > 0"
                  :error-message="sharesOutstandingErrors.join(' ')"
                  hint="Required for Earnings Per Share calculations"
                  @update:model-value="touch('sharesOutstanding')" />
              </div>
            </fieldset>
            <fieldset>
              <div>
                <h2>Weights &amp; Tolerances</h2>
                <p>
                  Input effective tax rate to account for insurance expense tax relief, and maximum allowable tolerance to highlight where financial
                  performance degrades beyond an acceptable limit.
                </p>
                <p>If actual tax rates are unknown, 21% is typically a good starting point.</p>
              </div>
              <div class="pt-2">
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.effectiveTaxRate"
                  suffix="%"
                  class="effective-tax-rate text-field"
                  data-testid="volatility-effective-tax-rate"
                  :error="effectiveTaxRateErrors.length > 0"
                  :error-message="effectiveTaxRateErrors.join(' ')"
                  @update:model-value="touch('effectiveTaxRate')"
                  label="* Effective Tax Rate" />
                <NumberInput
                  :disabled="saving"
                  placeholder=" "
                  v-model="volatilityModel.maximumTolerance"
                  suffix="%"
                  class="max-tolerance text-field"
                  data-testid="volatility-maximum-tolerance"
                  label="* Maximum Tolerance"
                  :error="maximumToleranceErrors.length > 0"
                  :error-message="maximumToleranceErrors.join(' ')"
                  @update:model-value="touch('maximumTolerance')" />
              </div>
            </fieldset>
          </div>
          <div class="buttons">
            <div class="sticky">
              <AppButton
                class="tw:btn-block"
                primary
                :loading="saving"
                :disabled="saving || v$.$invalid || !hasAtLeastOneValidAnnualPerformanceIndicator"
                type="submit">
                Save &amp; Generate
              </AppButton>
              <AppButton
                class="tw:btn-block tw:mt-2"
                secondary
                :disabled="saving"
                @click="cancel">
                Cancel
              </AppButton>
              <p
                class="text-error"
                v-if="!hasAtLeastOneValidAnnualPerformanceIndicator">
                Please enter at least one annual performance indicator.
              </p>
              <p
                class="text-error"
                v-else-if="v$.$anyError">
                Please correct errors to continue.
              </p>
              <p
                class="text-error"
                v-else-if="v$.$invalid && !v$.$anyError">
                Enter required fields to continue.
              </p>
              <p>
                If third-party party public financials requested and available, they will overwrite any annual performance indicators manually
                entered.
              </p>
              <p>* Required field</p>
              <p>All financial figures in $USD</p>
              <p>
                The volatility analytics model can be used to understand the impact on a company’s financial performance for a variety of different
                retention scenarios.
              </p>
              <p>
                Its output can assist when determining placement strategy to balance the benefits of a lower premium with the cost of an undesirable
                claims outcome.
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import NumberInput from '@/components/v2/NumberInput.vue';
import Combobox from '@/components/v2/Combobox.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Alert from '@/components/v2/Alert.vue';
import TextField from '@/components/v2/TextField.vue';
import { decimal, required, requiredIf } from '@vuelidate/validators';
import * as Errors from '@/utilities/Errors';
import { DateTime } from 'luxon';
import { isNumeric } from '@/utilities/Number';
import { accountService, volatilityModelService } from '@/services/api';
import PageProgress from '@/components/PageProgress';
import { useVuelidate } from '@vuelidate/core';
import { LucideBadgeCheck } from 'lucide-vue-next';

const isDate = (value) => !DateTime.fromFormat('' + value, 'm/dd/yyyy').invalid;

export default {
  name: 'VolatilityForm',
  components: { PageProgress, NumberInput, Combobox, LucideBadgeCheck, AppButton, Alert, TextField },
  emits: ['updated'],
  setup() {
    return {
      v$: useVuelidate(),
    };
  },
  data: () => ({
    loading: true,
    saving: false,
    clients: [],
    volatilityModel: {
      id: undefined,
      name: undefined,
      accountId: undefined,
      accountName: undefined,
      retention1: undefined,
      retention2: undefined,
      retention3: undefined,
      retention4: undefined,
      financialYearEnd: undefined,
      operatingIncome: undefined,
      netIncome: undefined,
      ebitda: undefined,
      ebitdaMargin: undefined,
      annualRevenue: undefined,
      cashFlowFromOperations: undefined,
      freeCashFlow: undefined,
      earningsPerShare: undefined,
      sharesOutstanding: undefined,
      effectiveTaxRate: undefined,
      maximumTolerance: undefined,
      insertedBy: undefined,
      insertedDate: undefined,
      updatedBy: undefined,
      updatedDate: undefined,
    },
    stockTicker: undefined,
    fetchedPublicData: undefined,
    publicDataLoading: false,
    snackbar: false,
    snackbarMsg: {
      title: '',
      subTitle: '',
      error: false,
    },
    publicData: {
      error: false,
      errorMessages: '',
    },
    errors: {
      financialYearEnd: [],
    },
  }),
  validations: {
    volatilityModel: {
      name: { required },
      accountName: { required },
      financialYearEnd: { required, isDate },
      retention1: { required, decimal },
      retention2: { decimal },
      retention3: { decimal },
      retention4: { decimal },
      operatingIncome: { decimal },
      netIncome: { decimal },
      ebitda: { decimal },
      cashFlowFromOperations: { decimal },
      freeCashFlow: { decimal },
      earningsPerShare: { decimal },
      ebitdaMargin: { decimal },
      annualRevenue: {
        required: requiredIf(function (_, model) {
          return !!model.ebitdaMargin;
        }),
        decimal,
      },
      sharesOutstanding: {
        required: requiredIf(function (_, model) {
          return !!model.earningsPerShare;
        }),
        decimal,
      },
      effectiveTaxRate: { required, decimal },
      maximumTolerance: { required, decimal },
    },
  },
  computed: {
    clientsForCombobox() {
      return this.clients.map((c) => ({
        label: c.stockTicker ? `${c.accountName} (${c.stockTicker})` : c.accountName,
        value: c.accountID.toString(),
      }));
    },
    accountNameModelValue() {
      if (typeof this.volatilityModel.accountName === 'object' && this.volatilityModel.accountName?.accountID) {
        return this.volatilityModel.accountName.accountID.toString();
      }
      if (typeof this.volatilityModel.accountName === 'string') {
        // Try to find matching client by name
        const client = this.clients.find((c) => this.clientDisplayName(c) === this.volatilityModel.accountName);
        return client ? client.accountID.toString() : this.volatilityModel.accountName;
      }
      return '';
    },
    isEditing() {
      return !!this.$route.params.volatilityId;
    },
    fromAccountView() {
      return !!this.$route.params.id;
    },
    isStockTickerEntered() {
      return this.stockTicker && this.stockTicker.trim() !== '';
    },
    accountNameMatch() {
      if (this.volatilityModel.accountName === null) return null;

      if (typeof this.volatilityModel.accountName === 'string') {
        return this.clients.some((c) => this.clientDisplayName(c) === this.volatilityModel.accountName) ? 'mdi-check-circle' : null;
      } else if (typeof this.volatilityModel.accountName === 'object' && 'accountName' in this.volatilityModel.accountName) {
        return this.clients.some((c) => this.clientDisplayName(c) === this.volatilityModel.accountName.accountName) ? 'mdi-check-circle' : null;
      }
      return null;
    },
    hasAtLeastOneValidAnnualPerformanceIndicator() {
      return ['operatingIncome', 'netIncome', 'ebitda', 'cashFlowFromOperations', 'freeCashFlow', 'earningsPerShare', 'ebitdaMargin'].some(
        (indicator) => isNumeric(this.volatilityModel[indicator]),
      );
    },
    nameErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.name.$dirty) return errors;
      if (!this.v$.volatilityModel.name.required) errors.push(Errors.FieldRequired);
      return errors;
    },
    accountNameErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.accountName.$dirty) return errors;
      if (!this.v$.volatilityModel.accountName.required) errors.push(Errors.FieldRequired);
      return errors;
    },
    retention1Errors() {
      const errors = [];
      if (!this.v$.volatilityModel.retention1.$dirty) return errors;
      if (!this.v$.volatilityModel.retention1.required) errors.push(Errors.FieldRequired);
      if (!this.v$.volatilityModel.retention1.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    retention2Errors() {
      const errors = [];
      if (!this.v$.volatilityModel.retention2.$dirty) return errors;
      if (!this.v$.volatilityModel.retention2.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    retention3Errors() {
      const errors = [];
      if (!this.v$.volatilityModel.retention3.$dirty) return errors;
      if (!this.v$.volatilityModel.retention3.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    retention4Errors() {
      const errors = [];
      if (!this.v$.volatilityModel.retention4.$dirty) return errors;
      if (!this.v$.volatilityModel.retention4.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    operatingIncomeErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.operatingIncome.$dirty) return errors;
      if (!this.v$.volatilityModel.operatingIncome.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    netIncomeErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.netIncome.$dirty) return errors;
      if (!this.v$.volatilityModel.netIncome.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    ebitdaErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.ebitda.$dirty) return errors;
      if (!this.v$.volatilityModel.ebitda.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    cashFlowFromOperationsErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.cashFlowFromOperations.$dirty) return errors;
      if (!this.v$.volatilityModel.cashFlowFromOperations.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    freeCashFlowErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.freeCashFlow.$dirty) return errors;
      if (!this.v$.volatilityModel.freeCashFlow.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    earningsPerShareErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.earningsPerShare.$dirty) return errors;
      if (!this.v$.volatilityModel.earningsPerShare.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    ebitdaMarginErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.ebitdaMargin.$dirty) return errors;
      if (!this.v$.volatilityModel.ebitdaMargin.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    annualRevenueErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.annualRevenue.$dirty) return errors;
      if (!this.v$.volatilityModel.annualRevenue.required) errors.push(Errors.FieldRequired);
      if (!this.v$.volatilityModel.annualRevenue.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    sharesOutstandingErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.sharesOutstanding.$dirty) return errors;
      if (!this.v$.volatilityModel.sharesOutstanding.required) errors.push(Errors.FieldRequired);
      if (!this.v$.volatilityModel.sharesOutstanding.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    effectiveTaxRateErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.effectiveTaxRate.$dirty) return errors;
      if (!this.v$.volatilityModel.effectiveTaxRate.required) errors.push(Errors.FieldRequired);
      if (!this.v$.volatilityModel.effectiveTaxRate.decimal) errors.push(Errors.Numeric);
      return errors;
    },
    maximumToleranceErrors() {
      const errors = [];
      if (!this.v$.volatilityModel.maximumTolerance.$dirty) return errors;
      if (!this.v$.volatilityModel.maximumTolerance.required) errors.push(Errors.FieldRequired);
      if (!this.v$.volatilityModel.maximumTolerance.decimal) errors.push(Errors.Numeric);
      return errors;
    },
  },
  methods: {
    handleAccountNameChange(value) {
      // If value is numeric (accountID), find the client and set the object
      if (value && !isNaN(value)) {
        const client = this.clients.find((c) => c.accountID.toString() === value);
        if (client) {
          this.volatilityModel.accountName = {
            accountName: this.clientDisplayName(client),
            accountID: client.accountID,
          };
          this.accountNameChange(client.stockTicker);
        }
      } else {
        // If it's a string (user typed something), set it as string
        this.volatilityModel.accountName = value || '';
      }
      this.touch('accountName');
    },
    touch() {
      for (const argument of arguments) {
        this.v$.volatilityModel[argument].$touch();
      }
    },
    cancel() {
      this.$router.back();
    },
    async save(e) {
      e.preventDefault();
      this.v$.$touch();
      if (!this.v$.$invalid) {
        this.saving = true;
        // if accountName is an object, then that means the user selected an existing account from the combobox
        // otherwise, accountName is a string and it should be sent to the server directly
        let accountName = this.volatilityModel.accountName;
        if (typeof accountName === 'object') {
          accountName = this.volatilityModel.accountName.accountName;
        }
        // try to find a client match. if found, then set the accountID and remove the accountName.
        // otherwise, remove the accountID and use the accountName
        const client = this.clients.find((c) => c.accountName.trim().toLowerCase() === accountName.trim().toLowerCase());

        let accountId;
        if (typeof this.volatilityModel.accountName === 'object') {
          accountId = this.volatilityModel.accountName.accountID;
        } else if (client) {
          accountId = client.accountID;
        }

        accountName = accountId ? null : accountName;
        // convert percentages
        const ebitdaMargin =
          ('' + this.volatilityModel.ebitdaMargin).trim().length > 0 ? parseFloat('' + this.volatilityModel.ebitdaMargin) / 100 : null;
        const effectiveTaxRate =
          ('' + this.volatilityModel.effectiveTaxRate).trim().length > 0 ? parseFloat('' + this.volatilityModel.effectiveTaxRate) / 100 : null;
        const maximumTolerance =
          ('' + this.volatilityModel.maximumTolerance).trim().length > 0 ? parseFloat('' + this.volatilityModel.maximumTolerance) / 100 : null;
        // convert to a json compatible (iso 8601) format.
        const financialYearEnd = DateTime.fromFormat(this.volatilityModel.financialYearEnd, 'M/dd/yyyy').toISODate();
        // save inserts or updates as needed based on if an id exists on the model
        const id = await volatilityModelService.save({
          ...this.volatilityModel,
          accountId,
          accountName,
          financialYearEnd,
          ebitdaMargin,
          effectiveTaxRate,
          maximumTolerance,
        });
        this.$emit('updated');
        const saveRoute = this.fromAccountView
          ? `/account/${this.$route.params.id}/analytics/category/${this.$route.params.categoryId}/volatility/${id}`
          : `/analytics/category/${this.$route.params.categoryId}/volatility/${id}`;
        return this.$router.push(saveRoute);
      }
    },
    async fetchPublicData() {
      if (!this.stockTicker || this.stockTicker.trim() === '') {
        return;
      }

      this.publicData = {};
      this.publicDataLoading = true;

      try {
        this.fetchedPublicData = await accountService.getPublicCompanyData(this.stockTicker.trim());
      } catch {
        this.snackbar = true;
        this.snackbarMsg = {
          title: 'An error occurred while retrieving public data.',
          error: true,
        };
        this.publicDataLoading = false;
        return;
      }

      if (this.fetchedPublicData.stockTicker === '') {
        this.publicData.error = true;
        this.publicData.errorMessages = 'No data found';
        this.publicDataLoading = false;

        this.volatilityModel = {
          operatingIncome: undefined,
          netIncome: undefined,
          ebitda: undefined,
          ebitdaMargin: undefined,
          annualRevenue: undefined,
          cashFlowFromOperations: undefined,
          freeCashFlow: undefined,
          earningsPerShare: undefined,
          sharesOutstanding: undefined,
          financialYearEnd: undefined,
          accountName: this.volatilityModel.accountName,
          name: this.volatilityModel.name,
        };
        return;
      }

      const data = this.fetchedPublicData;

      this.volatilityModel = {
        ...this.volatilityModel,
        operatingIncome: data.operatingIncome,
        netIncome: data.netIncome,
        ebitda: data.ebitda,
        ebitdaMargin: ((data.ebitda / data.annualRevenue) * 100).toFixed(1),
        annualRevenue: data.annualRevenue,
        cashFlowFromOperations: data.cashFlowFromOperations,
        freeCashFlow: data.freeCashFlow, // todo capex? + capex,
        earningsPerShare: data.earningsPerShare,
        sharesOutstanding: data.sharesOutstanding,
        financialYearEnd: (data.fiscalYearEnd && DateTime.fromISO(data.fiscalYearEnd).toFormat('MM/dd/yyyy')) || undefined,
      };
      this.publicDataLoading = false;
      this.publicData = {};
      this.snackbar = true;
      this.snackbarMsg = {
        title: 'Public Data Retrieved',
        subTitle: 'Validate third-party data before generating results.',
        error: false,
      };
    },
    accountNameChange(stockTicker) {
      if (stockTicker?.trim()) {
        this.stockTicker = stockTicker;
      } else {
        this.stockTicker = undefined;
      }
    },
    async refreshData() {
      this.loading = true;
      this.clients = await accountService.list();
      if (this.$route.params.volatilityId) {
        const model = await volatilityModelService.findById(this.$route.params.volatilityId);
        const financialYearEnd = DateTime.fromISO(model.financialYearEnd, { setZone: true }).toFormat('MM/dd/yyyy');
        const ebitdaMargin = model.ebitdaMargin * 100;
        const effectiveTaxRate = model.effectiveTaxRate * 100;
        const maximumTolerance = model.maximumTolerance * 100;
        this.volatilityModel = { ...model, financialYearEnd, ebitdaMargin, effectiveTaxRate, maximumTolerance };
      } else if (this.$route.params.id) {
        const account = await accountService.findById(this.$route.params.id);
        if (account) {
          this.volatilityModel.accountName = account.accountName;
          this.accountNameChange(account.stockTicker);
        }
      }
      this.loading = false;
    },
    validateFinancialYearEnd() {
      const errors = [];
      if (!this.v$.volatilityModel.financialYearEnd.$dirty) this.errors.financialYearEnd = [];
      if (!this.v$.volatilityModel.financialYearEnd.required) errors.push(Errors.FieldRequired);
      if (this.v$.volatilityModel.financialYearEnd.isDate.$invalid) errors.push(Errors.MMDDYYYY);

      this.errors.financialYearEnd = errors;
    },
    clientDisplayName(client) {
      return client.stockTicker ? `${client.accountName} (${client.stockTicker})` : client.accountName;
    },
    tickerSymbolCleared() {
      this.stockTicker = undefined;
    },
  },
  watch: {
    'volatilityModel.accountName'(newAccount) {
      const client = this.clients.find((c) => c.accountID === newAccount.accountID);

      if (client) {
        this.accountNameChange(client.stockTicker);
      }
    },
  },
  mounted() {
    return this.refreshData();
  },
};
</script>

<style scoped>
.content {
  max-width: 1280px;
  padding: 10px;
}

.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.form {
  display: grid;
  grid-template-columns: 1fr 225px;
  grid-column-gap: 2rem;
}

fieldset {
  border: none;
  display: grid;
  grid-template-columns: 225px 1fr;
  gap: 0 2rem;
  margin-bottom: 2.5rem;
  border-bottom: 1px solid #e5e7eb;
}

fieldset:last-child {
  border-bottom: none;
}

fieldset > div:nth-child(2) {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem 2rem;
  align-content: start;
}

.name {
  grid-area: name;
}

.client {
  grid-area: client;
}

.retention1 {
  grid-area: retention1;
}

.retention2 {
  grid-area: retention2;
}

.retention3 {
  grid-area: retention3;
}

.retention4 {
  grid-area: retention4;
}

.financialYearEnd {
  grid-area: financialYearEnd;
}

.public-data {
  grid-area: public-data;
}

.general-fields {
  grid-template-areas: 'name name' 'client public-data' 'financialYearEnd financialYearEnd' 'retention1 retention2' 'retention3 retention4';
  gap: 1.5rem 2rem;
  align-content: start;
  align-items: start;
}

@media (max-width: 1280px) {
  .general-fields {
    grid-template-areas: 'name name' 'client public-data' 'financialYearEnd financialYearEnd' 'retention1 retention2' 'retention3 retention4';
  }
}

fieldset > div > p {
  font-size: 0.9em;
}

.text-field {
  margin-bottom: 0;
}

.buttons {
  border-left: 1px solid #e5e7eb;
  padding: 0.5rem 0 4rem 1rem;
  text-align: left;
}

.buttons p {
  margin-top: 1rem;
  font-size: 0.9em;
}

.sticky {
  position: sticky;
  top: 100px;
}
</style>
